// camera-gps.ts
import * as piexif from 'piexifjs'

export const isTorchSupported = async () => {
	// Only test on secure contexts

	// /* eslint-disable no-restricted-globals */
	// eslint-disable-next-line no-restricted-globals
	if (!navigator?.mediaDevices || (location?.protocol !== 'https:' && (location?.origin.indexOf('localhost') < 0))) {
		console.warn('getUserMedia not available or not over HTTPS.');
		return false;
	}

	try {
		// Request rear camera access
		const stream = await navigator.mediaDevices.getUserMedia({
			video: { facingMode: { exact: "environment" } }
		});

		const track = stream.getVideoTracks()[0];
		const capabilities: any = track.getCapabilities();

		// Stop the camera immediately
		track.stop();

		// Check if torch is supported
		return !!capabilities.torch;
	} catch (err) {
		console.warn('Torch detection failed:', err);
		return false;
	}
}

export const goFullscreen = () => {
	const elem = document.documentElement; // or any other element
	if (elem.requestFullscreen) {
		elem.requestFullscreen();
	} else if ((elem as any).webkitRequestFullscreen) {
		(elem as any).webkitRequestFullscreen(); // Safari
	} else if ((elem as any).msRequestFullscreen) {
		(elem as any).msRequestFullscreen(); // IE11
	}
}

export const exitFullscreen = async (): Promise<boolean> => {
	try {
		// Check if document is currently in fullscreen
		const isFullscreen =
			document.fullscreenElement ||
			(document as any).webkitFullscreenElement ||
			(document as any).mozFullScreenElement ||
			(document as any).msFullscreenElement;

		// If not in fullscreen, return early
		if (!isFullscreen) {
			console.warn('Document is not in fullscreen mode');
			return false;
		}

		// Exit fullscreen with proper browser support
		if (document.exitFullscreen) {
			await document.exitFullscreen();
		} else if ((document as any).webkitExitFullscreen) {
			await (document as any).webkitExitFullscreen(); // Safari
		} else if ((document as any).mozCancelFullScreen) {
			await (document as any).mozCancelFullScreen(); // Firefox
		} else if ((document as any).msExitFullscreen) {
			await (document as any).msExitFullscreen(); // IE11
		}

		return true;
	} catch (error) {
		console.error('Error exiting fullscreen:', error);
		return false;
	}
};

export interface TakePhotoOptions {
	video: HTMLVideoElement
	canvas: HTMLCanvasElement
	quality?: number // 0..1, default 0.92
	geolocation?: PositionOptions // override defaults if needed
}

export const getExifBytes = async (): Promise<any> => {
	// 1) Try to get geolocation. If it fails, return the base JPEG.
	let pos: GeolocationPosition | null = null
	try {
		pos = await getCurrentPositionPromise({
			enableHighAccuracy: true,
			timeout: 10000,
			maximumAge: 0,
		})
	} catch {
		// Permission denied, timeout, or unsupported: return base image
		return null
	}

	// 2) Build EXIF with GPS
	const { latitude, longitude, altitude, accuracy, altitudeAccuracy, heading, speed } = pos.coords
	const when = new Date(pos.timestamp || Date.now())

	const exifObj: any = { '0th': {}, Exif: {}, GPS: {}, '1st': {}, thumbnail: null }

	exifObj.GPS[piexif.GPSIFD.GPSLatitudeRef] = latitude >= 0 ? 'N' : 'S'
	exifObj.GPS[piexif.GPSIFD.GPSLatitude] = toExifDMS(latitude)
	exifObj.GPS[piexif.GPSIFD.GPSLongitudeRef] = longitude >= 0 ? 'E' : 'W'
	exifObj.GPS[piexif.GPSIFD.GPSLongitude] = toExifDMS(longitude)

	// Date tags
	const dt = toExifDateTime(when)
	exifObj.Exif[piexif.ExifIFD.DateTimeOriginal] = dt
	exifObj['0th'][piexif.ImageIFD.DateTime] = dt

	// Optional altitude
	if (Number.isFinite(altitude)) {
		const alt = altitude as number
		exifObj.GPS[piexif.GPSIFD.GPSAltitudeRef] = alt >= 0 ? 0 : 1 // 0=above sea level, 1=below
		exifObj.GPS[piexif.GPSIFD.GPSAltitude] = [Math.round(Math.abs(alt) * 100), 100]
	}

	// Optional heading
	if (Number.isFinite(heading)) {
		exifObj.GPS[piexif.GPSIFD.GPSImgDirectionRef] = 'T'
		exifObj.GPS[piexif.GPSIFD.GPSImgDirection] = [Math.round((heading as number) * 100), 100]
	}

	// Optional speed (store in km/h)
	if (Number.isFinite(speed)) {
		const kmh = (speed as number) * 3.6
		exifObj.GPS[piexif.GPSIFD.GPSSpeedRef] = 'K'
		exifObj.GPS[piexif.GPSIFD.GPSSpeed] = [Math.round(kmh * 100), 100]
	}

	// Non-standard but helpful: put accuracy in UserComment
	if (Number.isFinite(accuracy)) {
		const parts = [`ACC=${Math.round((accuracy as number))}m`]
		if (Number.isFinite(altitudeAccuracy)) parts.push(`ACC_ALT=${Math.round((altitudeAccuracy as number))}m`)
		exifObj.Exif[piexif.ExifIFD.UserComment] = 'ASCII\0\0\0' + parts.join(';')
	}

	const exifBytes = piexif.dump(exifObj)
	return exifBytes
}

export const addExifBytes = (exifBytes: any, baseDataUrl: any): string => {
	// Insert EXIF into the JPEG data URL and return
	return piexif.insert(exifBytes, baseDataUrl)
}

/* Utilities */

export const getCurrentPositionPromise = (options?: PositionOptions): Promise<GeolocationPosition> => {
	return new Promise((resolve, reject) => {
		if (!('geolocation' in navigator)) {
			reject(new Error('Geolocation not supported'))
			return
		}
		navigator.geolocation.getCurrentPosition(resolve, reject, options)
	})
}

// Convert decimal degrees to EXIF DMS rational format [[deg,1],[min,1],[sec*100,100]]
export const toExifDMS = (value: number): [number[], number[], number[]] => {
	const abs = Math.abs(value)
	const deg = Math.floor(abs)
	const minFloat = (abs - deg) * 60
	const min = Math.floor(minFloat)
	const sec = Math.round((minFloat - min) * 60 * 100) // 2 decimals
	return [[deg, 1], [min, 1], [sec, 100]]
}

// EXIF datetime "YYYY:MM:DD HH:MM:SS"
export const toExifDateTime = (d: Date): string => {
	const pad = (n: number) => String(n).padStart(2, '0')
	return `${d.getFullYear()}:${pad(d.getMonth() + 1)}:${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}
